import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  getCategories,
  addCategory,
  deleteCategory,
  updateCategory,
} from "../apinew";
import { Tag, Plus, Save, X, Edit2, Trash2 } from "lucide-react";

const TAG_COLORS = [
  { bg: "#dbeafe", text: "#1e40af", border: "#93c5fd" },
  { bg: "#dcfce7", text: "#166534", border: "#86efac" },
  { bg: "#fef3c7", text: "#92400e", border: "#fde68a" },
  { bg: "#fee2e2", text: "#991b1b", border: "#fecaca" },
  { bg: "#f3e8ff", text: "#6b21a8", border: "#d8b4fe" },
  { bg: "#fce7f3", text: "#9f1239", border: "#fbcfe8" },
];

export default function CategoriesPage() {
  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
  const creatorId = currentUser?.user_id || currentUser?.id || "";

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    category_id: null,
    creator_id: creatorId,
    name: "",
    type: "income",
  });

  const resetForm = () =>
    setForm({
      category_id: null,
      creator_id: creatorId,
      name: "",
      type: "income",
    });

  const load = async () => {
    try {
      setLoading(true);
      const res = await getCategories();
      const list = Array.isArray(res) ? res : res?.data ?? [];
      setCategories(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error("Gagal load kategori:", err);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleAddOrUpdate = async () => {
    if (!form.name) return alert("Nama kategori wajib diisi");

    const payload = {
      creator_id: creatorId,
      name: form.name,
      type: form.type,
    };

    try {
      setSaving(true);

      if (form.category_id) {
        await updateCategory(form.category_id, payload);
      } else {
        await addCategory(payload);
      }

      resetForm();
      await load();
    } catch (err) {
      console.error("Gagal simpan kategori:", err);
      alert(
        err.response?.data?.message || err.message || "Gagal menyimpan kategori"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (c) => {
    setForm({
      category_id: c.category_id,
      creator_id: creatorId,
      name: c.name || "",
      type: c.type || "income",
    });
  };

  const handleDelete = async (id) => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm("Yakin ingin menghapus kategori ini?")) return;

    try {
      await deleteCategory(id);
      await load();
    } catch (err) {
      console.error("Gagal hapus kategori:", err);
      alert(
        err.response?.data?.message || err.message || "Gagal menghapus kategori"
      );
    }
  };

  const getTagColor = (idx) => TAG_COLORS[idx % TAG_COLORS.length];

  return (
    <div style={{ padding: "24px", minHeight: "100vh", background: "#f8fafc" }}>
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        style={{ marginBottom: "24px" }}
      >
        <h2
          style={{
            fontSize: "24px",
            fontWeight: "700",
            color: "#0f172a",
            marginBottom: "4px",
          }}
        >
          Kategori
        </h2>
        <p style={{ fontSize: "14px", color: "#64748b", margin: 0 }}>
          Atur transaksi Anda dengan kategori
        </p>
      </motion.div>

      {/* Form Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        style={{
          background: "#ffffff",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          padding: "24px",
          marginBottom: "24px",
          boxShadow:
            "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
        }}
      >
        <h3
          style={{
            fontSize: "16px",
            fontWeight: "600",
            color: "#0f172a",
            marginBottom: "20px",
          }}
        >
          {form.category_id ? "Edit Kategori" : "Tambah Kategori Baru"}
        </h3>

        <div style={{ display: "flex", gap: "12px", alignItems: "flex-end" }}>
          <div style={{ flex: 1 }}>
            <label
              style={{
                display: "block",
                fontSize: "13px",
                fontWeight: "500",
                color: "#374151",
                marginBottom: "6px",
              }}
            >
              Nama Kategori
            </label>
            <input
              placeholder="Masukkan nama kategori"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              onFocus={(e) => {
                e.currentTarget.style.border = "1px solid #3b82f6";
                e.currentTarget.style.boxShadow =
                  "0 0 0 3px rgba(59, 130, 246, 0.1)";
              }}
              onBlur={(e) => {
                e.currentTarget.style.border = "1px solid #e2e8f0";
                e.currentTarget.style.boxShadow = "none";
              }}
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "1px solid #e2e8f0",
                borderRadius: "6px",
                fontSize: "14px",
                color: "#0f172a",
                outline: "none",
                transition: "all 0.2s ease",
              }}
            />
          </div>

          <div style={{ width: "180px" }}>
            <label
              style={{
                display: "block",
                fontSize: "13px",
                fontWeight: "500",
                color: "#374151",
                marginBottom: "6px",
              }}
            >
              Tipe
            </label>
            <select
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
              onFocus={(e) => {
                e.currentTarget.style.border = "1px solid #3b82f6";
                e.currentTarget.style.boxShadow =
                  "0 0 0 3px rgba(59, 130, 246, 0.1)";
              }}
              onBlur={(e) => {
                e.currentTarget.style.border = "1px solid #e2e8f0";
                e.currentTarget.style.boxShadow = "none";
              }}
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "1px solid #e2e8f0",
                borderRadius: "6px",
                fontSize: "14px",
                color: "#0f172a",
                background: "#ffffff",
                outline: "none",
                transition: "all 0.2s ease",
              }}
            >
              <option value="income">Pemasukan</option>
              <option value="expense">Pengeluaran</option>
            </select>
          </div>

          {form.category_id && (
            <button
              onClick={resetForm}
              onMouseEnter={(e) => {
                e.currentTarget.style.opacity = "0.9";
                e.currentTarget.style.boxShadow = "0 2px 6px rgba(0,0,0,0.08)";
                e.currentTarget.style.transform = "translateY(-1px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = "1";
                e.currentTarget.style.boxShadow = "none";
                e.currentTarget.style.transform = "translateY(0)";
              }}
              style={{
                padding: "10px 16px",
                border: "1px solid #e2e8f0",
                borderRadius: "6px",
                background: "#ffffff",
                color: "#64748b",
                fontSize: "14px",
                fontWeight: "500",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                transition: "all 0.2s ease",
              }}
            >
              <X size={16} />
              Batal
            </button>
          )}

          <button
            onClick={handleAddOrUpdate}
            disabled={saving}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = "0.9";
              e.currentTarget.style.boxShadow = form.category_id
                ? "0 2px 6px rgba(245,158,11,0.3)"
                : "0 2px 6px rgba(59,130,246,0.3)";
              e.currentTarget.style.transform = "translateY(-1px)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = saving ? 0.7 : 1;
              e.currentTarget.style.boxShadow = "none";
              e.currentTarget.style.transform = "translateY(0)";
            }}
            style={{
              padding: "10px 16px",
              border: "none",
              borderRadius: "6px",
              background: form.category_id ? "#f59e0b" : "#3b82f6",
              color: "#ffffff",
              fontSize: "14px",
              fontWeight: "500",
              cursor: saving ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              opacity: saving ? 0.7 : 1,
              transition: "all 0.2s ease",
            }}
          >
            {form.category_id ? <Save size={16} /> : <Plus size={16} />}
            {saving ? "Menyimpan..." : form.category_id ? "Perbarui" : "Tambah"}
          </button>
        </div>
      </motion.div>

      {/* Categories Grid */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        {loading ? (
          <div
            style={{ padding: "48px", textAlign: "center", color: "#94a3b8" }}
          >
            Memuat kategori...
          </div>
        ) : categories.length === 0 ? (
          <div
            style={{
              background: "#ffffff",
              borderRadius: "12px",
              border: "1px solid #e2e8f0",
              padding: "48px",
              textAlign: "center",
              color: "#94a3b8",
            }}
          >
            Belum ada kategori. Tambahkan kategori pertama Anda di atas.
          </div>
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
            {categories.map((cat, idx) => {
              const color = getTagColor(idx);
              return (
                <motion.div
                  key={cat.category_id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: idx * 0.03 }}
                  style={{
                    background: color.bg,
                    border: `1px solid ${color.border}`,
                    borderRadius: "8px",
                    padding: "12px 16px",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    position: "relative",
                    minWidth: "180px",
                  }}
                >
                  <Tag size={16} color={color.text} strokeWidth={2} />
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px",
                      flex: 1,
                    }}
                  >
                    <span
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: color.text,
                      }}
                    >
                      {cat.name}
                    </span>
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                        padding: "4px 8px",
                        borderRadius: "999px",
                        background: "rgba(255,255,255,0.6)",
                        color: color.text,
                        fontSize: "12px",
                        fontWeight: "600",
                        width: "fit-content",
                      }}
                    >
                      {cat.type === "expense" ? "Pengeluaran" : "Pemasukan"}
                    </span>
                  </div>

                  <div style={{ display: "flex", gap: "4px" }}>
                    <button
                      onClick={() => handleEdit(cat)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.opacity = "0.9";
                        e.currentTarget.style.boxShadow =
                          "0 2px 6px rgba(245,158,11,0.25)";
                        e.currentTarget.style.transform = "translateY(-1px)";
                        e.currentTarget.style.background = "#fde68a";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.opacity = "1";
                        e.currentTarget.style.boxShadow = "none";
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.background = "rgba(0,0,0,0.05)";
                      }}
                      style={{
                        padding: "4px 8px",
                        border: "none",
                        borderRadius: "4px",
                        background: "rgba(0,0,0,0.05)",
                        color: color.text,
                        fontSize: "12px",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                      }}
                    >
                      <Edit2 size={12} />
                    </button>
                    <button
                      onClick={() => handleDelete(cat.category_id)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.opacity = "0.9";
                        e.currentTarget.style.boxShadow =
                          "0 2px 6px rgba(220,38,38,0.25)";
                        e.currentTarget.style.transform = "translateY(-1px)";
                        e.currentTarget.style.background = "#fecaca";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.opacity = "1";
                        e.currentTarget.style.boxShadow = "none";
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.background = "rgba(0,0,0,0.05)";
                      }}
                      style={{
                        padding: "4px 8px",
                        border: "none",
                        borderRadius: "4px",
                        background: "rgba(0,0,0,0.05)",
                        color: color.text,
                        fontSize: "12px",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                      }}
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </motion.div>
    </div>
  );
}
